import { useEffect, useState } from "react";
import { Check, Image as ImageIcon, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { EmptyState } from "./EmptyState";
import { api } from "../../lib/api";

// Sélecteur visuel de la Médiathèque centrale — réutilisé par les formulaires admin
// (blog, partenaires, réalisations…). Le champ URL des formulaires reste disponible en complément.
// Ne liste que les médias ACTIFS (jamais les archivés).
// multiple=true : sélection multi-images par clic (toggle) + bouton de validation groupée ;
// onSelect reçoit alors un tableau de médias (sinon un seul média).
export const MediaPickerDialog = ({ open, onOpenChange, onSelect, title = "Choisir depuis la Médiathèque", multiple = false }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setQ("");
    setItems(null);
    api.get("/admin/media")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data.items || [];
        setItems(list.filter((m) => !m.archived));
      })
      .catch(() => setItems([]));
  }, [open]);

  const filtered = (items || []).filter((m) => (m.name || "").toLowerCase().includes(q.toLowerCase()));
  const isSelected = (m) => selected.some((s) => s.id === m.id);
  const toggle = (m) => {
    setSelected((prev) => {
      if (!multiple) return [m];
      return prev.some((s) => s.id === m.id) ? prev.filter((s) => s.id !== m.id) : [...prev, m];
    });
  };
  const confirm = () => {
    onSelect(multiple ? selected : selected[0]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream max-w-3xl" data-testid="media-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-deepgreen">{title}</DialogTitle>
          <DialogDescription>
            {multiple
              ? "Cliquez sur les images pour les sélectionner (plusieurs possibles), puis validez."
              : "Seuls les médias actifs de la Médiathèque sont proposés. Le champ URL reste utilisable en complément."}
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input className="input-ae pl-9" placeholder="Rechercher un média…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="media-picker-search" aria-label="Rechercher un média" />
        </div>
        {items === null ? (
          <p className="py-10 text-center font-heading text-deepgreen">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="media-picker-empty">
            <EmptyState title="Aucun média actif">Ajoutez d'abord une photo dans la Médiathèque (« Ajouter un média » → « Depuis mon appareil »).</EmptyState>
          </div>
        ) : (
          <div className="grid max-h-[50vh] grid-cols-2 gap-3 overflow-y-auto p-0.5 sm:grid-cols-3" data-testid="media-picker-grid">
            {filtered.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => toggle(m)}
                data-testid={`media-picker-item-${m.id}`}
                className={`rounded-lg border-2 p-1.5 text-left transition-colors ${isSelected(m) ? "border-deepgreen bg-sage-light" : "border-transparent bg-white hover:border-sage"}`}
              >
                <div className="relative overflow-hidden rounded-md">
                  <img src={m.url} alt={m.alt || m.name} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                  {isSelected(m) && (
                    <span className="absolute right-1.5 top-1.5 rounded-full bg-deepgreen p-1 text-cream" data-testid={`media-picker-check-${m.id}`}>
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                  {multiple && isSelected(m) && (
                    <span className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-deepgreen text-[10px] font-bold text-cream" data-testid={`media-picker-order-${m.id}`}>
                      {selected.findIndex((s) => s.id === m.id) + 1}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 truncate px-0.5 text-xs font-semibold text-deepgreen">{m.name}</p>
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          disabled={selected.length === 0}
          onClick={confirm}
          className="btn-primary-ae w-full disabled:opacity-50"
          data-testid="media-picker-confirm"
        >
          <ImageIcon className="h-4 w-4" />
          {multiple
            ? `Utiliser ${selected.length > 0 ? `ces ${selected.length} image${selected.length > 1 ? "s" : ""}` : "cette sélection"}`
            : "Utiliser cette image"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
